import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import PageHeader from "shared/PageHeader";
import Alert from "shared/Alert";
import FilterChip from "shared/FilterChip";
import StatusPill from "shared/StatusPill";
import Button from "shared/Button";
import { FORM_CONTROL } from "shared/tableStyles";
import { cn } from "lib/utils";
import { apiErrorMessage } from "./apiError";
import { showSuccess } from "shared/toast";
import {
  getConversation, getConversationInbox, postMessageAnalysis, type Conversation, type ConversationInbox, type ConversationOutcome,
} from "./api/OutreachTouchesApi";
import { formatKarachi } from "./outreachDates";
import LinkedInHubTabs from "./ui/LinkedInHubTabs";
import ConversationThread from "./ui/ConversationThread";
import StatusHelp from "./ui/StatusHelp";
import ConversationActions, { OUTCOME_LABEL, STALE_INTEREST_HINT, staleInterest } from "./ui/ConversationActions";
import { resultStatus, turnStatus } from "./ui/conversationStatus";

const PAGE_SIZE = 25;
const CHANNEL_LABEL = { email: "Email", linkedin: "LinkedIn" } as const;

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

function who(c: Conversation) {
  return c.contact_name || c.contact_email || "Unknown contact";
}

/** The Conversations inbox: every conversation with a contact, open and closed, as a list and a chat-style thread.  */
export default function LinkedInConversationsPage() {
  // Filters and the chosen conversation live in the URL; several keys change at once, so every change is one `patch` write.
  const [params, setParams] = useSearchParams();
  const rawState = params.get("state");
  const state = (rawState === "closed" || rawState === "open" ? rawState : "all") as "open" | "closed" | "all";      // no parameter = All
  const outcome = (params.get("outcome") ?? "") as ConversationOutcome | "none" | "";
  const waiting = (params.get("waiting") === "us" || params.get("waiting") === "them" ? params.get("waiting") : "") as "us" | "them" | "";
  const noAnswer = params.get("quiet") === "1";
  const rfp = params.get("rfp") ?? "";
  const q = params.get("q") ?? "";
  const selected = Number.parseInt(params.get("c") ?? "", 10);
  const selectedId = Number.isFinite(selected) && selected >= 1 ? selected : null;
  const patch = (changes: Record<string, string | number | null>) => setParams((prev) => {
    const p = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v === null || v === "") p.delete(k); else p.set(k, String(v)); }
    return p;
  }, { replace: true });

  const [typed, setTyped] = useState(q);
  const [data, setData] = useState<ConversationInbox | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [thread, setThread] = useState<Conversation | null>(null);
  const [threadError, setThreadError] = useState("");
  const [skipping, setSkipping] = useState(false);
  const [refresh, setRefresh] = useState(0);        // bumped after a change so the list (counts, order, pills) reloads
  const latest = useRef(0);
  const latestThread = useRef(0);
  const loadedPages = useRef(0);                 // how many pages of the list are on screen (infinite scroll)
  const loadFailed = useRef(false);               // the last "load more" failed: do not retry by itself, only when the person scrolls again
  const filterKey = useRef("");
  const listRef = useRef<HTMLElement | null>(null);
  const selectedRef = useRef<number | null>(null);
  selectedRef.current = selectedId;

  useEffect(() => { setTyped(q); }, [q]);
  useEffect(() => {
    const t = setTimeout(() => { if (typed !== q) patch({ q: typed, page: null }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, q]);

  const query = (p: number) => getConversationInbox({
    state, outcome: outcome || undefined, waitingOn: waiting || undefined, noAnswer, opportunityId: rfp ? Number(rfp) : undefined,
    search: q, page: p, pageSize: PAGE_SIZE,
  });

  // New filters start again from the first page; a reload after a change (refresh) re-fetches every page already on screen,
  // so the list keeps its length and the scroll position.
  useEffect(() => {
    const mine = ++latest.current;      // a slower, older answer must never overwrite a newer one
    const key = [state, outcome, waiting, noAnswer, rfp, q].join("|");
    const same = key === filterKey.current;
    filterKey.current = key;
    loadFailed.current = false;
    const keep = same ? Math.max(1, loadedPages.current) : 1;
    setLoading(true);
    Promise.all(Array.from({ length: keep }, (_, i) => query(i + 1)))
      .then((results) => {
        if (mine !== latest.current) return;
        const seen = new Set<number>();
        const items = results.flatMap((r) => r.items).filter((c) => !seen.has(c.id) && !!seen.add(c.id));
        loadedPages.current = Math.min(keep, results[0].total_pages);
        setData({ ...results[0], items });
        setError("");
        if (!same && listRef.current) listRef.current.scrollTop = 0;
      })
      .catch((e) => { if (mine === latest.current) setError(apiErrorMessage(e, "Could not load the conversations.")); })
      .finally(() => { if (mine === latest.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, outcome, waiting, noAnswer, rfp, q, refresh]);

  const hasMore = !!data && loadedPages.current < data.total_pages;
  function loadMore(byPerson = false) {
    if (byPerson) loadFailed.current = false;
    if (!data || loading || loadingMore || loadedPages.current >= data.total_pages) return;
    const mine = latest.current;
    const next = loadedPages.current + 1;
    setLoadingMore(true);
    query(next)
      .then((d) => {
        if (mine !== latest.current) return;              // the filters changed meanwhile
        loadedPages.current = next;
        setData((prev) => {
          if (!prev) return prev;
          const have = new Set(prev.items.map((c) => c.id));
          return { ...prev, items: [...prev.items, ...d.items.filter((c) => !have.has(c.id))], counts: d.counts, total_count: d.total_count, total_pages: d.total_pages };
        });
      })
      .catch((e) => { loadFailed.current = true; setError(apiErrorMessage(e, "Could not load more conversations.")); })
      .finally(() => setLoadingMore(false));
  }
  const nearEnd = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight < 240;
  // a tall screen may show the whole first page without any scrolling: keep filling until it overflows or the list ends
  useEffect(() => {
    if (hasMore && !loadFailed.current && !loading && !loadingMore && listRef.current && listRef.current.scrollHeight > 0 && listRef.current.scrollHeight <= listRef.current.clientHeight) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, loadingMore]);

  useEffect(() => {
    if (selectedId === null) { setThread(null); setThreadError(""); return; }
    const mine = ++latestThread.current;
    setThread(null);
    setThreadError("");
    getConversation(selectedId)
      .then((c) => { if (mine === latestThread.current) setThread(c); })
      .catch((e) => { if (mine === latestThread.current) setThreadError(apiErrorMessage(e, "Could not load this conversation.")); });
  }, [selectedId]);

  const filtered = !!(outcome || waiting || noAnswer || rfp || q || state !== "all");
  const reset = () => { setTyped(""); setParams(selectedId ? { c: String(selectedId) } : {}, { replace: true }); };
  // One choice: "No answer" is a part of "Waiting on them" (a silent wait), so the three never combine.
  const turn = noAnswer ? "quiet" : waiting;
  const pickTurn = (v: string) => patch({ waiting: v === "us" || v === "them" ? v : null, quiet: v === "quiet" ? "1" : null, page: null });
  const narrow = (key: string) => (v: string) => patch({ [key]: v, page: null });
  const counts = data?.counts;
  const open = (id: number) => patch({ c: id });
  /** A change was saved in the thread of conversation `forId`: reload the list, and show the fresh conversation (a redirect starts a NEW one,
   *  which is opened). If the person has moved to another conversation meanwhile, only the list reloads. */
  function changed(forId: number, updated?: Conversation, kind?: "redirect") {
    setRefresh((n) => n + 1);
    if (selectedRef.current !== forId) return;
    if (updated && kind === "redirect") { patch({ c: updated.id }); return; }
    if (updated) setThread(updated);
    else getConversation(forId).then((c) => { if (selectedRef.current === forId) setThread(c); }).catch(() => undefined);
  }

  async function skipAnalysis(conversationId: number, messageId: number) {
    setSkipping(true);
    setThreadError("");
    try {
      await postMessageAnalysis(messageId, { status: "skipped", note: "Nothing to learn from this message" });
      showSuccess("Marked as nothing to analyse");
      changed(conversationId);
    } catch (e) {
      setThreadError(apiErrorMessage(e, "Could not save that. Reload and try again."));
    } finally {
      setSkipping(false);
    }
  }

  return (
    <div className="space-y-6">
      <div><LinkedInHubTabs /></div>
      <PageHeader
        title="LinkedIn Outreach"
        description={data ? `Every conversation with a contact · ${data.total_count} ${data.total_count === 1 ? "conversation" : "conversations"}${filtered ? " match" : ""}` : "Loading…"}
      />
      {error && <Alert tone="danger" role="alert">{error}</Alert>}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Show">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Show</span>
        <FilterChip active={state === "all"} onClick={() => narrow("state")("")} count={counts?.all}>All</FilterChip>
        <FilterChip active={state === "open"} onClick={() => narrow("state")("open")} count={counts?.open}>Open</FilterChip>
        <FilterChip active={state === "closed"} onClick={() => narrow("state")("closed")} count={counts?.closed}>Closed</FilterChip>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Search people, companies or messages" aria-label="Search the conversations"
            className={`pl-8 pr-3 py-1.5 w-80 ${FORM_CONTROL}`} />
        </div>
        <select value={rfp} onChange={(e) => narrow("rfp")(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`} aria-label="Filter by RFP">
          <option value="">All RFPs</option>
          {(data?.rfps ?? []).map((r) => <option key={r.id} value={r.id}>{r.title ?? `RFP ${r.id}`}</option>)}
        </select>
        <select value={turn} onChange={(e) => pickTurn(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`} aria-label="Filter by whose turn">
          <option value="">Whose turn: All</option>
          <option value="us">Waiting on us{counts ? ` (${counts.waiting_on_us})` : ""}</option>
          <option value="them">Waiting on them{counts ? ` (${counts.waiting_on_them})` : ""}</option>
          <option value="quiet">No answer{counts ? ` (${counts.no_answer})` : ""}</option>
        </select>
        <select value={outcome} onChange={(e) => narrow("outcome")(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`} aria-label="Filter by outcome">
          <option value="">Status: All</option>
          <option value="none">Open (no result yet)</option>
          {(["interested", "interested_other_rfp", "not_interested", "not_interested_all", "teaming", "no_response", "removed", "redirected"] as ConversationOutcome[]).map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
        </select>
        <StatusHelp className="self-center" />
        {state === "closed" && turn && <span className="text-xs text-muted-foreground">Closed conversations have no turn, so nothing matches.</span>}
        {filtered && <Button variant="ghost" size="sm" onClick={reset}>Clear filters</Button>}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(18rem,24rem)_1fr]">
        {/* the list; on a phone it gives way to the thread while one is open */}
        <section ref={listRef} aria-label="Conversations" onScroll={(e) => { if (nearEnd(e.currentTarget)) loadMore(true); }}
          className={cn("space-y-2 max-h-[calc(100vh-15rem)] min-h-[20rem] overflow-y-auto overscroll-contain pr-1 thin-scroll", selectedId !== null && "hidden lg:block", loading && data && "opacity-60 transition-opacity")} aria-busy={loading}>
          {data && data.items.length === 0 && (
            <p className="rounded-xl border border-border bg-surface px-4 py-10 text-center text-sm text-muted-foreground">
              {filtered ? "No conversation matches these filters." : "No conversation has started yet. One starts when a reply is logged."}
            </p>
          )}
          {!data && !error && <p className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</p>}
          <ul className="space-y-2">
            {(data?.items ?? []).map((c) => (
              <li key={c.id}>
                <div role="button" tabIndex={0} data-testid={`inbox-${c.id}`} aria-current={selectedId === c.id ? "true" : undefined} onClick={() => open(c.id)}
                  onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); open(c.id); } }}
                  className={cn("w-full cursor-pointer rounded-xl border px-3 py-2.5 text-left transition-colors hover:bg-surface-muted",
                    selectedId === c.id ? "border-primary bg-primary-soft" : "border-border bg-surface")}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 truncate text-sm font-semibold text-foreground">{who(c)}</p>
                    <span className="shrink-0 text-xs text-muted-foreground">{c.last_message_at ? formatKarachi(c.last_message_at).replace(/ \d{4},/, ",") : ""}</span>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {c.company_name} · <Link className="underline underline-offset-2 hover:text-foreground" to={`${crmBase()}?opportunity_id=${c.opportunity_id}`}
                      onClick={(e) => e.stopPropagation()} title="Open the RFP details">{c.rfp_title}</Link>
                  </p>
                  {c.last_message_preview && (
                    <p className="mt-1 line-clamp-2 text-sm text-foreground">
                      <span className="text-xs text-muted-foreground">{c.last_sender === "us" ? "You: " : ""}</span>{c.last_message_preview}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {turnStatus(c) && <StatusPill tone={turnStatus(c)!.tone} size="sm">{turnStatus(c)!.label}</StatusPill>}
                    {resultStatus(c.outcome) && <StatusPill tone={resultStatus(c.outcome)!.tone} size="sm">{resultStatus(c.outcome)!.label}</StatusPill>}
                    {staleInterest(c) && <StatusPill tone="warning" size="sm" title={STALE_INTEREST_HINT}>RFP closed, update the result</StatusPill>}
                    {c.pending_analysis > 0 && <StatusPill tone="info" size="sm" title="Run /rfp-prime-reply-analysis to learn from this reply">Analysis pending</StatusPill>}
                    {c.last_channel && <span className="text-xs text-muted-foreground">{CHANNEL_LABEL[c.last_channel]}</span>}
                    <span className="text-xs text-muted-foreground">· {c.message_count} {c.message_count === 1 ? "message" : "messages"}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {loadingMore && <p className="py-2 text-center text-xs text-muted-foreground" role="status">Loading more…</p>}
          {data && !hasMore && data.total_pages > 1 && <p className="py-2 text-center text-xs text-muted-foreground">That is all {data.total_count} conversations.</p>}
        </section>

        <section aria-label="Conversation" className={cn("min-w-0 rounded-xl border border-border bg-surface", selectedId === null && "hidden lg:block")}>
          {selectedId === null && <p className="px-4 py-16 text-center text-sm text-muted-foreground">Choose a conversation to read it.</p>}
          {selectedId !== null && threadError && <div className="p-4"><Alert tone="danger" role="alert">{threadError}</Alert></div>}
          {selectedId !== null && !threadError && !thread && <p className="px-4 py-16 text-center text-sm text-muted-foreground">Loading the conversation…</p>}
          {thread && (
            <div className="flex flex-col">
              <header className="space-y-1.5 border-b border-border px-4 py-3">
                <Button variant="ghost" size="sm" className="lg:hidden -ml-2" onClick={() => patch({ c: null })}><ArrowLeft className="w-4 h-4 mr-1" aria-hidden />All conversations</Button>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-foreground">{who(thread)}</h2>
                    <p className="truncate text-xs text-muted-foreground">
                      <Link className="underline underline-offset-2" to={`${crmBase()}/companies/${thread.prime_company_id}`}>{thread.company_name}</Link>
                      {" · "}
                      <Link className="underline underline-offset-2" to={`${crmBase()}?opportunity_id=${thread.opportunity_id}`}>{thread.rfp_title}</Link>
                      {thread.contact_name && thread.contact_email ? ` · ${thread.contact_email}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {turnStatus(thread) && <StatusPill tone={turnStatus(thread)!.tone} size="sm">{turnStatus(thread)!.label}</StatusPill>}
                    {resultStatus(thread.outcome) ? <StatusPill tone={resultStatus(thread.outcome)!.tone} size="sm">{resultStatus(thread.outcome)!.label}</StatusPill> : <span className="text-xs text-muted-foreground">Result: Open</span>}
                    {staleInterest(thread) && <StatusPill tone="warning" size="sm" title={STALE_INTEREST_HINT}>RFP closed, update the result</StatusPill>}
                  </div>
                </div>
              </header>
              <div className="max-h-[70vh] overflow-y-auto px-4 py-4">
                <ConversationThread messages={thread.messages ?? []} who={who(thread)} busy={skipping} onSkipAnalysis={(id) => void skipAnalysis(thread.id, id)} />
              </div>
              <div className="border-t border-border px-4 py-3">
                <ConversationActions key={thread.id} conversation={thread} onChanged={(updated, kind) => changed(thread.id, updated, kind)} />
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
